import React, { createContext, useContext, useEffect, useState } from "react";
import Keycloak from "keycloak-js";
import { identifyUser, captureException, startNewSession } from "./logrocket";
import FrozenDeedLoading from "../components/FrozenDeedLoading";

interface KeycloakConfigFile {
  KEYCLOAK_BASE_URL: string;
  KEYCLOAK_REALM: string;
  KEYCLOAK_CLIENT_ID: string;
}

interface KeycloakContextProps {
  keycloak: Keycloak | null;
  initialized: boolean;
  getToken: () => Promise<string | null | undefined>;
  login: () => void;
  logout: () => void;
}

const KeycloakContext = createContext<KeycloakContextProps>({
  keycloak: null,
  initialized: false,
  getToken: async () => null,
  login: () => {},
  logout: () => {},
});

export const useKeycloak = () => useContext(KeycloakContext);

function useConfig(): KeycloakConfigFile | null {
  const [config, setConfig] = useState<KeycloakConfigFile | null>(null);
  useEffect(() => {
    fetch("/config.json")
      .then((res) => res.json())
      .then((data) => setConfig(data));
  }, []);
  return config;
}

// ---------------------------------------------------------------------------
// HRD result — persisted in sessionStorage so it survives Keycloak's redirect
// ---------------------------------------------------------------------------
interface HrdResult { realm: string; email: string; }

const HRD_SESSION_KEY = 'fzd_hrd';

function saveHrd(result: HrdResult) {
  try { sessionStorage.setItem(HRD_SESSION_KEY, JSON.stringify(result)); } catch {}
}
function loadHrd(): HrdResult | null {
  try {
    const raw = sessionStorage.getItem(HRD_SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function clearHrd() {
  try { sessionStorage.removeItem(HRD_SESSION_KEY); } catch {}
}

// ---------------------------------------------------------------------------
// Logo Component (replaces missing /logo.svg)
// ---------------------------------------------------------------------------
function Logo({ className, fill = "white" }: { className?: string, fill?: string }) {
  return (
    <svg className={className} viewBox="0 0 126 44" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M33.8038 36.1856C33.8038 32.4209 33.7708 29.0147 33.8275 25.6104C33.8422 24.7451 33.544 24.2311 32.8708 23.75C29.9494 21.6609 27.0408 19.5518 24.1633 17.4005C23.4389 16.859 22.9468 16.8627 22.2096 17.4243C19.3504 19.5975 16.4491 21.7176 13.5131 23.7884C12.8381 24.2659 12.6149 24.7634 12.6222 25.5409C12.6533 28.5227 12.6424 31.5044 12.6277 34.4844C12.6259 34.938 12.7668 35.4191 12.443 35.9295C11.3801 35.3789 10.6649 34.7624 10.7124 33.3722C10.8131 30.4416 10.7637 27.5056 10.7289 24.5714C10.7216 23.8835 10.9338 23.45 11.5009 23.0494C15.1668 20.4573 18.8181 17.8487 22.4438 15.1998C23.0145 14.7828 23.4206 14.7974 23.9475 15.1761C27.6646 17.856 31.3891 20.5286 35.1154 23.1976C35.5343 23.4976 35.7227 23.8616 35.7191 24.3921C35.7008 27.5147 35.6898 30.6392 35.7191 33.7618C35.7319 35.0021 34.7331 35.3277 33.8019 36.1838L33.8038 36.1856Z" fill={fill}/>
      <path d="M31.2099 26.7136C31.2135 26.1117 31.1001 25.6837 30.544 25.294C28.2994 23.7208 26.086 22.1019 23.9073 20.4391C23.3164 19.989 22.9524 20.1244 22.4438 20.5013C20.3163 22.0745 18.1724 23.6275 16.0156 25.1605C15.4522 25.5611 15.2656 26.0367 15.2839 26.7154C15.3278 28.3709 15.2986 30.0283 15.2986 31.6838C15.2986 32.7247 15.2986 33.7655 15.2986 34.8064C15.2986 38.3205 15.3059 38.2894 18.7559 39.1675C19.6468 39.3943 26.6896 39.7766 30.1233 38.3169C30.8349 38.015 31.2501 37.7004 31.2318 36.7912C31.1623 33.4326 31.1952 30.0722 31.2117 26.7117L31.2099 26.7136ZM25.5097 30.1636C24.7488 30.7435 24.7433 31.2905 24.928 32.0789C25.1512 33.032 25.2116 34.0235 25.4384 34.9765C25.6469 35.8583 25.3268 36.1199 24.4981 36.0394C24.0683 35.9973 23.6329 36.0321 23.1993 36.0321C21.6676 36.026 21.0286 35.2827 21.2822 33.8021C21.3481 33.4161 21.4084 33.0265 21.5017 32.646C21.756 31.6198 21.9591 30.7783 20.8542 29.8984C19.623 28.9197 19.9395 27.0648 21.2164 25.9617C22.4841 24.8678 24.5969 25.0141 25.6982 26.2727C26.7409 27.4618 26.7354 29.2307 25.5116 30.1636H25.5097Z" fill={fill}/>
      <path d="M34.0926 8.14793C31.5115 6.12472 28.5443 4.94482 25.515 4.55701C29.8834 5.70398 33.1158 9.68821 33.1158 14.4115V17.9768L29.9108 16.0213V14.4115C29.9108 10.5608 26.7772 7.42719 22.9265 7.42719C19.0758 7.42719 15.9422 10.5608 15.9422 14.4115V16.003L12.8818 18.0079L12.7391 14.4115C12.7391 9.99004 15.5709 6.21985 19.5131 4.81311C15.3313 5.7113 11.444 8.11135 8.73844 11.8779C3.59626 19.0396 5.42923 29.002 9.26711 32.4265C9.26711 29.6313 9.30919 26.8855 9.24516 24.1416C9.22321 23.1793 9.54517 22.5866 10.319 22.0397C14.2995 19.2299 18.2636 16.3963 22.1875 13.5078C22.9851 12.9206 23.479 12.9773 24.2546 13.548C28.1766 16.4383 32.1334 19.2829 36.1232 22.0799C36.9116 22.6324 37.2463 23.1848 37.2226 24.1525C37.1585 26.8947 37.2006 29.6386 37.2006 32.6679C42.8276 25.6471 42.0629 14.3914 34.0926 8.14793Z" fill={fill}/>
      <path d="M104.476 20.2123V7.72464H105.85L113.024 17.8153L112.404 17.8824V7.72464H114.064V20.2123H112.689L105.549 10.0545L106.118 9.9875V20.2123H104.476Z" fill={fill}/>
      <path d="M94.3107 20.2123V7.72464H102.44V9.23321H95.9533V13.189H102.105V14.6976H95.9533V18.7037H102.44V20.2123H94.3107Z" fill={fill}/>
      <path d="M83.5145 20.2123V19.0054L90.3031 9.23321H83.9168V7.72464H92.4319V8.9315L85.6768 18.7037H92.5995V20.2123H83.5145Z" fill={fill}/>
      <path d="M75.8389 20.4134C74.9561 20.4134 74.1292 20.257 73.3582 19.9441C72.5871 19.62 71.9111 19.1674 71.33 18.5864C70.7601 18.0053 70.3131 17.3236 69.989 16.5414C69.665 15.748 69.5029 14.8876 69.5029 13.9601C69.5029 13.0214 69.665 12.161 69.989 11.3787C70.3131 10.5965 70.7601 9.91486 71.33 9.33378C71.9111 8.7527 72.5815 8.30572 73.3414 7.99283C74.1125 7.67994 74.945 7.5235 75.8389 7.5235C76.7329 7.5235 77.5598 7.68553 78.3197 8.00959C79.0907 8.32248 79.7612 8.76947 80.3311 9.35055C80.9122 9.92045 81.3648 10.5965 81.6888 11.3787C82.0241 12.161 82.1917 13.0214 82.1917 13.9601C82.1917 14.8876 82.0241 15.748 81.6888 16.5414C81.3648 17.3236 80.9122 18.0053 80.3311 18.5864C79.7612 19.1674 79.0907 19.62 78.3197 19.9441C77.5598 20.257 76.7329 20.4134 75.8389 20.4134ZM75.8389 18.8881C76.5318 18.8881 77.1631 18.7652 77.733 18.5193C78.3029 18.2623 78.7946 17.9103 79.2081 17.4633C79.6327 17.0051 79.9568 16.4799 80.1803 15.8877C80.4149 15.2843 80.5323 14.6417 80.5323 13.9601C80.5323 13.2784 80.4149 12.6415 80.1803 12.0492C79.9568 11.457 79.6327 10.9373 79.2081 10.4904C78.7946 10.0322 78.3029 9.6802 77.733 9.43436C77.1631 9.17734 76.5318 9.04883 75.8389 9.04883C75.1573 9.04883 74.5315 9.17734 73.9616 9.43436C73.3917 9.6802 72.8944 10.0322 72.4698 10.4904C72.0563 10.9373 71.7323 11.457 71.4976 12.0492C71.2629 12.6415 71.1456 13.2784 71.1456 13.9601C71.1456 14.6417 71.2629 15.2843 71.4976 15.8877C71.7323 16.4799 72.0563 17.0051 72.4698 17.4633C72.8944 17.9103 73.3917 18.2623 73.9616 18.5193C74.5315 18.7652 75.1573 18.8881 75.8389 18.8881Z" fill={fill}/>
      <path d="M59.0027 20.2123V7.72464H63.4111C64.238 7.72464 64.9643 7.88108 65.5901 8.19397C66.2159 8.50686 66.702 8.95385 67.0484 9.53493C67.406 10.116 67.5848 10.7977 67.5848 11.5799C67.5848 12.4627 67.3613 13.2114 66.9143 13.826C66.4673 14.4406 65.8639 14.882 65.104 15.1502L68.0038 20.2123H66.093L63.0423 14.7982L64.1318 15.4351H60.6454V20.2123H59.0027ZM60.6454 13.9265H63.4614C63.953 13.9265 64.3833 13.8316 64.752 13.6416C65.1208 13.4516 65.4057 13.1778 65.6069 12.8203C65.8192 12.4627 65.9254 12.0492 65.9254 11.5799C65.9254 11.0994 65.8192 10.6859 65.6069 10.3395C65.4057 9.98191 65.1208 9.70813 64.752 9.51816C64.3833 9.3282 63.953 9.23321 63.4614 9.23321H60.6454V13.9265Z" fill={fill}/>
      <path d="M49.2302 20.2123V7.72464H57.1418V9.23321H50.8729V13.2728H56.5552V14.7814H50.8729V20.2123H49.2302Z" fill={fill}/>
      <path d="M81.3625 36.4211V23.9335H85.4357C86.7878 23.9335 87.9612 24.2017 88.9557 24.738C89.9502 25.2744 90.7213 26.0119 91.2688 26.9506C91.8164 27.8893 92.0902 28.962 92.0902 30.1689C92.0902 31.3758 91.8164 32.4541 91.2688 33.4039C90.7213 34.3426 89.9502 35.0801 88.9557 35.6165C87.9612 36.1529 86.7878 36.4211 85.4357 36.4211H81.3625ZM83.9606 34.1582H85.5027C86.3185 34.1582 87.0225 33.9962 87.6147 33.6721C88.207 33.3369 88.6652 32.8732 88.9892 32.2809C89.3245 31.6775 89.4921 30.9735 89.4921 30.1689C89.4921 29.3532 89.3245 28.6492 88.9892 28.0569C88.6652 27.4646 88.207 27.0065 87.6147 26.6824C87.0225 26.3584 86.3185 26.1963 85.5027 26.1963H83.9606V34.1582Z" fill={fill}/>
      <path d="M71.4921 36.4211V23.9335H79.8898V26.1963H74.0902V29.0291H79.5545V31.2919H74.0902V34.1582H79.8898V36.4211H71.4921Z" fill={fill}/>
      <path d="M61.6215 36.4211V23.9335H70.0192V26.1963H64.2196V29.0291H69.6839V31.2919H64.2196V34.1582H70.0192V36.4211H61.6215Z" fill={fill}/>
      <path d="M49.2302 36.4211V23.9334H53.3034C54.6555 23.9334 55.8288 24.2016 56.8234 24.738C57.8179 25.2744 58.589 26.0119 59.1365 26.9506C59.6841 27.8893 59.9578 28.962 59.9578 30.1689C59.9578 31.3757 59.6841 32.4541 59.1365 33.4039C58.589 34.3426 57.8179 35.0801 56.8234 35.6165C55.8288 36.1529 54.6555 36.4211 53.3034 36.4211H49.2302ZM51.8283 34.1582H53.3704C54.1862 34.1582 54.8902 33.9962 55.4824 33.6721C56.0747 33.3369 56.5328 32.8731 56.8569 32.2809C57.1921 31.6774 57.3597 30.9734 57.3597 30.1689C57.3597 29.3531 57.1921 28.6491 56.8569 28.0569C56.5328 27.4646 56.0747 27.0065 55.4824 26.6824C54.8902 26.3583 54.1862 26.1963 53.3704 26.1963H51.8283V34.1582Z" fill={fill}/>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// EmailGate — shown before Keycloak init to determine the correct realm
// ---------------------------------------------------------------------------
function EmailGate({ onRealm }: { onRealm: (result: HrdResult) => void }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      setError("Please enter a valid email address");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/hrd?email=${encodeURIComponent(email.trim())}`);
      if (!res.ok) throw new Error("HRD lookup failed");
      const data = await res.json();
      onRealm({ realm: data.realm as string, email: email.trim() });
    } catch (err) {
      console.error("[HRD] Realm lookup failed, using default realm:", err);
      onRealm({ realm: "identifid-shared", email: email.trim() });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
        {/* ── Left brand panel (matches Keycloakify AuthLayout) ──────────────── */}
        <div
            className="hidden lg:flex lg:w-[45%] flex-col justify-between p-12 relative overflow-hidden"
            style={{ background: "linear-gradient(145deg, #070D20 0%, #0a1a30 60%, #07C3C3 180%)" }}
        >
            <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-10"
                style={{ background: "radial-gradient(circle, #07C3C3, transparent)" }} />
            <div className="absolute -bottom-16 -left-16 w-64 h-64 rounded-full opacity-10"
                style={{ background: "radial-gradient(circle, #07C3C3, transparent)" }} />

            <div className="relative z-10">
                <Logo className="h-8 w-auto" fill="white" />
            </div>

            <div className="relative z-10">
                <p className="text-white/40 text-xs uppercase tracking-widest mb-3 font-medium">
                    Identity Platform
                </p>
                <h2 className="text-white text-2xl font-bold leading-snug">
                    Secure identity,<br />
                    <span style={{ color: "#07C3C3" }}>everywhere you need it.</span>
                </h2>
                <p className="mt-4 text-white/50 text-sm leading-relaxed max-w-xs">
                    Enterprise-grade biometric authentication and identity verification for modern organizations.
                </p>
            </div>
        </div>

        {/* ── Right form panel ─────────────────────────────────────────── */}
        <div className="flex-1 flex flex-col items-center justify-center p-6 sm:p-10 bg-gray-50">
            <div className="mb-8 lg:hidden">
                <Logo className="h-7 w-auto" fill="#070D20" />
            </div>

            <div className="card w-full max-w-md p-8 sm:p-10 animate-slide-up bg-white rounded-lg border shadow-card">
                <p className="text-xs text-gray-400 uppercase tracking-widest font-medium mb-6">
                    Secure Sign In
                </p>
                <h1 className="text-2xl font-bold text-[#070D20] mb-1">
                    Welcome
                </h1>
                <p className="text-sm text-[#485D7D] mb-8">
                    Enter your email to continue.
                </p>

                <form onSubmit={handleSubmit}>
                    <div className="mb-5 animate-slide-up-delay-1">
                        <label htmlFor="hrd-email-input" className="block text-sm font-medium text-[#485D7D] mb-2">
                            Email
                        </label>
                        <input
                            id="hrd-email-input"
                            type="email"
                            placeholder="you@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={loading}
                            autoFocus
                            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#07C3C3] focus:border-[#07C3C3] outline-none transition-shadow text-[#070D20]"
                        />
                    </div>
                    {error && (
                        <div className="mb-6 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600 animate-slide-up">
                            {error}
                        </div>
                    )}
                    <button
                        id="hrd-continue-btn"
                        type="submit"
                        disabled={loading}
                        className="w-full py-3 text-base font-semibold inline-flex items-center justify-center rounded-full px-4 text-white shadow hover:opacity-90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                        style={{ backgroundColor: "#07C3C3" }}
                    >
                        {loading ? "Looking up…" : "Continue"}
                    </button>
                </form>
            </div>

            <p className="mt-6 text-xs text-gray-400">
                © {new Date().getFullYear()} Identifid, Inc. All rights reserved.
            </p>
        </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// KeycloakProvider
// ---------------------------------------------------------------------------
export const KeycloakProvider = ({ children }: { children: React.ReactNode }) => {
  const [keycloak, setKeycloak] = useState<Keycloak | null>(null);
  const [initialized, setInitialized] = useState(false);
  // Seed from sessionStorage immediately so we survive the Keycloak redirect
  const [hrdResult, setHrdResultState] = useState<HrdResult | null>(loadHrd);
  const config = useConfig();

  // Wrapper that always persists to sessionStorage
  const setHrdResult = (result: HrdResult) => {
    saveHrd(result);
    setHrdResultState(result);
  };

  const getToken = async () => {
    if (!keycloak) return null;
    try {
      if (keycloak.isTokenExpired()) {
        await keycloak.updateToken(70);
      }
      return keycloak.token;
    } catch (error) {
      console.error("Failed to get token", error);
      captureException(error).catch(err => {
        console.warn('Failed to capture exception in LogRocket:', err);
      });
      await keycloak.logout();
      return null;
    }
  };

  const login = () => keycloak?.login();
  const logout = () => {
    clearHrd();
    setHrdResultState(null);
    startNewSession().catch(error => {
      console.warn('Failed to start new LogRocket session on logout:', error);
    });
    keycloak?.logout({ redirectUri: window.location.origin });
  };

  // Initialize Keycloak once config + hrdResult are available
  useEffect(() => {
    if (!config || !hrdResult || initialized) return;
    const { realm, email } = hrdResult;

    const keycloakInstance = new Keycloak({
      url: config.KEYCLOAK_BASE_URL,
      realm: realm,
      clientId: config.KEYCLOAK_CLIENT_ID,
    });

    keycloakInstance
      .init({
        onLoad: "check-sso",
        checkLoginIframe: false,
      })
      .then((authenticated) => {
        if (!authenticated) {
          // About to leave — sessionStorage already has realm, will be restored on return
          keycloakInstance.login({ loginHint: email });
          return;
        }

        // Authenticated — we keep the stored HRD in sessionStorage 
        // so that window.location.reload() doesn't kick the user back to the EmailGate.
        setKeycloak(keycloakInstance);
        setInitialized(true);

        if (keycloakInstance.tokenParsed?.preferred_username) {
          identifyUser(keycloakInstance.tokenParsed.preferred_username, {
            name: keycloakInstance.tokenParsed.name || keycloakInstance.tokenParsed.preferred_username,
            email: keycloakInstance.tokenParsed.email,
            realm: realm,
            clientId: config.KEYCLOAK_CLIENT_ID,
          }).catch(error => {
            console.warn('Failed to identify user in LogRocket:', error);
          });
        }

        keycloakInstance.onTokenExpired = () => {
          keycloakInstance
            .updateToken(70)
            .then((refreshed) => {
              if (refreshed) {
                console.log("Token refreshed " + refreshed);
              } else {
                console.log("Token not refreshed, valid for " + keycloakInstance.tokenParsed?.exp + " seconds");
                startNewSession().catch(error => {
                  console.warn('Failed to start new LogRocket session on token expiration:', error);
                });
              }
            })
            .catch((error) => {
              console.log("Failed to refresh the token, or the session has expired");
              captureException(error).catch(err => {
                console.warn('Failed to capture exception in LogRocket:', err);
              });
              startNewSession().catch(err => {
                console.warn('Failed to start new LogRocket session on token refresh failure:', err);
              });
            });
        };
      })
      .catch((error) => {
        console.error("Failed to initialize Keycloak", error);
        captureException(error).catch(err => {
          console.warn('Failed to capture exception in LogRocket:', err);
        });
      });

    const handleAuth401 = () => {
      console.warn("401 Unauthorized detected globally. Forcing logout.");
      logout();
    };

    window.addEventListener('auth-401', handleAuth401);
    
    return () => {
      window.removeEventListener('auth-401', handleAuth401);
    };
  }, [config, hrdResult, initialized]);

  // Show email gate until realm is determined
  if (!hrdResult) {
    return <EmailGate onRealm={setHrdResult} />;
  }

  return (
    <KeycloakContext.Provider value={{ keycloak, initialized, getToken, login, logout }}>
      {initialized ? children : <FrozenDeedLoading />}
    </KeycloakContext.Provider>
  );
};